import { useMutation } from "@apollo/client";
import type {
  CreateOrganizationSecurePayload,
  Organization,
} from "@casey/db-types";
import {
  CREATE_ORGANIZATION,
  GET_USER_ORGANIZATIONS,
  UPSERT_USER,
} from "../gql";

interface CreateOrganizationInput {
  name: string;
  ownerId: string;
  slug?: string;
  description?: string;
}

interface CreateOrganizationVariables {
  name: string;
  ownerId: string;
  slug?: string;
  description?: string;
}

interface CreateOrganizationResponse {
  createOrganizationSecure: CreateOrganizationSecurePayload;
}

export function useCreateOrganization() {
  const [createOrganizationMutation, { data, loading, error }] = useMutation<
    CreateOrganizationResponse,
    CreateOrganizationVariables
  >(CREATE_ORGANIZATION, {
    // Automatically refetch user organizations after creation
    refetchQueries: [{ query: GET_USER_ORGANIZATIONS }],
    errorPolicy: "all",
  });

  const createOrganization = async (
    organizationData: CreateOrganizationInput,
  ) => {
    try {
      const result = await createOrganizationMutation({
        variables: {
          name: organizationData.name,
          ownerId: organizationData.ownerId,
          slug: organizationData.slug,
          description: organizationData.description,
        },
      });

      const createdOrganization =
        result.data?.createOrganizationSecure?.organization;
      return createdOrganization;
    } catch (err) {
      console.error("Error creating organization:", err);
      throw err;
    }
  };

  return {
    createOrganization,
    data: data?.createOrganizationSecure?.organization,
    loading,
    error,
  };
}

export type { CreateOrganizationInput, Organization };
